import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { apiGet, apiPost } from '../../api/client';
import type { OrgPwaTokenRow } from './superOrgTypes';
import { superOrgBtn, superOrgField, superOrgInput } from './superOrgPanels';
import type { SuperOrgDetailOutletContext } from './SuperOrgDetailLayout';

export function SuperOrgPwaTokensPage() {
  const { org, token, setPageErr } = useOutletContext<SuperOrgDetailOutletContext>();
  const [items, setItems] = useState<OrgPwaTokenRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState('');
  const [label, setLabel] = useState('');
  const [expiresAtLocal, setExpiresAtLocal] = useState('');
  const [creating, setCreating] = useState(false);
  const [opaqueModal, setOpaqueModal] = useState<string | null>(null);

  const load = useCallback(async () => {
    setPageErr('');
    try {
      const data = await apiGet<{ items: OrgPwaTokenRow[] }>(
        `/api/super/organizations/${org.id}/pwa-tokens`,
        token
      );
      setItems(data.items);
    } catch (ex) {
      setPageErr((ex as Error).message);
    } finally {
      setLoading(false);
    }
  }, [org.id, token, setPageErr]);

  useEffect(() => {
    if (org.users.length > 0 && !userId) {
      setUserId(org.users[0]!.id);
    }
  }, [org.users, userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const createToken = useCallback(async () => {
    if (!userId) {
      setPageErr('Elegí un usuario de la empresa.');
      return;
    }
    setPageErr('');
    setCreating(true);
    try {
      let expiresAt: string | undefined;
      if (expiresAtLocal.trim()) {
        const d = new Date(expiresAtLocal);
        if (Number.isNaN(d.getTime())) {
          setPageErr('Fecha de caducidad inválida.');
          setCreating(false);
          return;
        }
        expiresAt = d.toISOString();
      }
      const data = await apiPost<{ token: string }>(
        `/api/super/organizations/${org.id}/pwa-tokens`,
        {
          userId,
          label: label.trim() || undefined,
          expiresAt: expiresAt ?? undefined,
        },
        token,
        { successMessage: 'Token PWA generado' }
      );
      setOpaqueModal(data.token);
      setLabel('');
      setExpiresAtLocal('');
      await load();
    } catch (ex) {
      setPageErr((ex as Error).message);
    } finally {
      setCreating(false);
    }
  }, [org.id, token, userId, label, expiresAtLocal, load, setPageErr]);

  const revoke = useCallback(
    async (row: OrgPwaTokenRow) => {
      if (row.revokedAt) return;
      if (!window.confirm('¿Revocar este token? La PWA dejará de poder renovar sesión con él.')) return;
      setPageErr('');
      try {
        await apiPost(
          `/api/super/organizations/${org.id}/pwa-tokens/${row.id}/revoke`,
          {},
          token,
          { successMessage: 'Token revocado' }
        );
        await load();
      } catch (ex) {
        setPageErr((ex as Error).message);
      }
    },
    [org.id, token, load, setPageErr]
  );

  return (
    <div>
      {opaqueModal ? (
        <div
          role="dialog"
          aria-modal
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 24,
              maxWidth: 520,
              width: '100%',
              border: '1px solid #e2e8f0',
              boxShadow: '0 20px 50px rgba(0,0,0,0.12)',
            }}
          >
            <h3 style={{ margin: '0 0 8px', fontSize: 17, color: '#0f172a' }}>Token PWA (solo una vez)</h3>
            <p style={{ margin: '0 0 12px', fontSize: 14, color: '#64748b' }}>
              Copiá este valor y entregalo al operador. No se puede recuperar desde la plataforma.
            </p>
            <code
              style={{
                display: 'block',
                padding: '12px 14px',
                background: '#f8fafc',
                borderRadius: 8,
                fontSize: 13,
                wordBreak: 'break-all',
                marginBottom: 16,
                border: '1px solid #e2e8f0',
              }}
            >
              {opaqueModal}
            </code>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                style={superOrgBtn}
                onClick={() => {
                  void navigator.clipboard.writeText(opaqueModal);
                }}
              >
                Copiar
              </button>
              <button type="button" style={{ ...superOrgBtn, background: '#64748b' }} onClick={() => setOpaqueModal(null)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <h2 style={{ fontSize: 17, margin: '0 0 8px', color: '#0f172a' }}>Tokens PWA (app empresa)</h2>
      <p style={{ color: '#64748b', fontSize: 14, margin: '0 0 20px' }}>
        Cada token está vinculado a un usuario de consola: la PWA usa las mismas notificaciones y permisos que ese
        usuario. Abrí la app en <code style={{ fontSize: 13 }}>http://localhost:3012</code> (o la URL que tengan
        desplegada) e iniciá sesión pegando el token.
      </p>

      <h3 style={{ fontSize: 15, margin: '0 0 12px' }}>Nuevo token</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', marginBottom: 28 }}>
        <label style={superOrgField}>
          Usuario
          <select
            style={{ ...superOrgInput, minWidth: 220 }}
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          >
            {org.users.length === 0 ? <option value="">Sin usuarios</option> : null}
            {org.users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.email} ({u.role})
              </option>
            ))}
          </select>
        </label>
        <label style={superOrgField}>
          Etiqueta (opcional)
          <input style={superOrgInput} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Móvil gerencia" />
        </label>
        <label style={superOrgField}>
          Caduca (opcional)
          <input
            style={superOrgInput}
            type="datetime-local"
            value={expiresAtLocal}
            onChange={(e) => setExpiresAtLocal(e.target.value)}
          />
        </label>
        <button type="button" style={superOrgBtn} disabled={creating || org.users.length === 0} onClick={() => void createToken()}>
          {creating ? 'Generando…' : 'Generar token'}
        </button>
      </div>

      <h3 style={{ fontSize: 15, margin: '0 0 12px' }}>Tokens emitidos</h3>
      {loading ? (
        <p style={{ color: '#94a3b8', fontSize: 14 }}>Cargando…</p>
      ) : items.length === 0 ? (
        <p style={{ color: '#94a3b8', fontSize: 14 }}>Todavía no hay tokens.</p>
      ) : (
        <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left', background: '#f8fafc', color: '#64748b' }}>
                <th style={{ padding: '10px 14px' }}>Usuario</th>
                <th style={{ padding: '10px 14px' }}>Etiqueta</th>
                <th style={{ padding: '10px 14px' }}>Alta</th>
                <th style={{ padding: '10px 14px' }}>Caduca</th>
                <th style={{ padding: '10px 14px' }}>Estado</th>
                <th style={{ padding: '10px 14px' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px 14px' }}>{row.user.email}</td>
                  <td style={{ padding: '10px 14px' }}>{row.label?.trim() || '—'}</td>
                  <td style={{ padding: '10px 14px', color: '#64748b' }}>{new Date(row.createdAt).toLocaleString()}</td>
                  <td style={{ padding: '10px 14px', color: '#64748b' }}>
                    {row.expiresAt ? new Date(row.expiresAt).toLocaleString() : '—'}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    {row.revokedAt ? (
                      <span style={{ color: '#b91c1c', fontWeight: 600 }}>Revocado</span>
                    ) : row.expiresAt && new Date(row.expiresAt).getTime() < Date.now() ? (
                      <span style={{ color: '#64748b' }}>Caducado</span>
                    ) : (
                      <span style={{ color: '#15803d', fontWeight: 600 }}>Vigente</span>
                    )}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    {!row.revokedAt ? (
                      <button
                        type="button"
                        style={{ ...superOrgBtn, fontSize: 12, padding: '6px 10px', background: '#b91c1c' }}
                        onClick={() => void revoke(row)}
                      >
                        Revocar
                      </button>
                    ) : (
                      <span style={{ color: '#94a3b8' }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
